import { engineComponent } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { Reel } from "../GameComponents/Reel";
import { Symbol } from "../GameComponents/Symbol";
import { Settings } from "./Settings";
import { Tweens } from "../Components/Effects/Tweens";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../Utils/FrameTimer";
import { Symbols } from "../GameComponents/Symbols";
import { ContainerElement } from "../Components/Elements/Graphics/ContainerElement";
import { PlayList } from "../Components/Sounds/PlayList";
import { SymbolExtended } from "./SymbolExtended";

@engineComponent(ComponentTypes.DisplayObject)
export class ReelExtended extends Reel {
    public static readonly ReelRollFinished: string = "reelRollFinished";

    public static Tease: boolean = false;

    private hasTeasingSymbol: boolean = false;
    private spinCompleteTimer: FrameTimerEventEmitter;

    private _symbolsNoTrimContainer: ContainerElement;

    private forced: boolean = false;

    public canTeasingSymbolHit: boolean = false;
    public countOfTeasingSymbols: number = 0;

    //******************************************************************************************************************
    //  INITIALIZE
    //******************************************************************************************************************

    public initialize(
        previousReel: Reel,
        nextReel: Reel,
        index: number,
        symbols: Symbols,
        strip: Array<number>,
        positionX: number,
        positionY: number,
        symbolCount: number,
        symbolTopCount: number,
        symbolBottomCount: number,
        symbolWidth: number,
        symbolHeight: number,
        startDelay: number,
        stopDelay: number,
        symbolsNoTrimContainer?: ContainerElement,
    ): void {
        super.initialize(
            previousReel,
            nextReel,
            index,
            symbols,
            strip,
            positionX,
            positionY,
            symbolCount,
            symbolTopCount,
            symbolBottomCount,
            symbolWidth,
            symbolHeight,
            startDelay,
            stopDelay,
        );

        this.spinCompleteTimer = CreateFrameTimer("spinCompleteTimer");
        this.spinCompleteTimer.on(FrameTimerEvents.Complete, this.spinComplete.bind(this));

        if (symbolsNoTrimContainer != null) {
            this._symbolsNoTrimContainer = symbolsNoTrimContainer;
        }
    }

    //******************************************************************************************************************
    //  SHOW SYMBOLS
    //******************************************************************************************************************

    public showSymbols(useTransform: boolean = false): void {
        this.shouldBeStopping = false;
        this.isStopping = false;
        this.stoppingCounter = 0;
        this.isTeasing = false;
        this.teasingSymbols = [];
        this.forced = false;

        this.x = this.positionX;
        this.y = this.positionY;

        while (this.currentSymbols.length > 0) {
            this.returnSymbol(this.currentSymbols.pop());
        }

        for (let i = 0; i < this.symbolBottomCount + this.symbolCount + this.symbolTopCount; i++) {
            let symbolId: number;
            let symbolCase: string;
            let fromStrip: boolean = true;

            if (i < this.symbolBottomCount) {
                symbolId =
                    this.strip[
                        (this.data.index + this.symbolBottomCount + this.symbolCount - i - 1 + this.strip.length) %
                            this.strip.length
                    ];
                symbolCase = i === this.symbolBottomCount - 1 ? Symbol.CaseRotation : Symbol.CaseBlur;
            } else if (i < this.symbolBottomCount + this.symbolCount) {
                symbolId = this.data.symbols[this.symbolBottomCount + this.symbolCount - i - 1];
                symbolCase = Symbol.CaseRotation;
                fromStrip = false;
            } else {
                symbolId =
                    this.strip[
                        (this.data.index + this.symbolBottomCount + this.symbolCount - i - 1 + this.strip.length) %
                            this.strip.length
                    ];
                symbolCase = i === this.symbolBottomCount + this.symbolCount ? Symbol.CaseRotation : Symbol.CaseBlur;
            }

            this.currentSymbols.unshift(this.getSymbol(symbolId - 1, symbolCase, fromStrip));

            //this.addChild(this.currentSymbols[0]);
            this.addChildAt(this.currentSymbols[0], 0);
        }

        for (let i = 0; i < this.currentSymbols.length; i++) {
            const symbol = this.currentSymbols[i];
            symbol.x = -symbol.offsetX;
            symbol.y = this.symbolHeight * (i - this.symbolTopCount) - symbol.offsetY;
        }

        this.sortSymbols();
    }

    //******************************************************************************************************************
    //  SPIN START
    //******************************************************************************************************************

    public spinStart(): void {
        this.hasTeasingSymbol = false;
        this.countOfTeasingSymbols = 0;

        super.spinStart();
    }

    //******************************************************************************************************************
    //  SPIN STOP
    //******************************************************************************************************************

    public spinStop(force: boolean = false, customDelay: number = 0, useForceCustomDelay: boolean = false): void {
        this.forced = force;

        super.spinStop(force, customDelay, useForceCustomDelay);
    }

    //******************************************************************************************************************
    //  SPIN POST ROLL
    //******************************************************************************************************************

    protected spinPostRoll(): void {
        let speed: number = this.postRollSpeed * 2; // (this.isTeasing && ReelExtended.Tease && !this.forced) ? this.teasingPostSpeed : (this.forced ? this.postRollSpeedForce : this.postRollSpeed);
        let post: number = this.postRoll; // (this.isTeasing && ReelExtended.Tease && !this.forced) ? this.teasingPost : (this.forced ? this.postRollForce : this.postRoll);
        let ease: string = "linear"; // (this.isTeasing && ReelExtended.Tease && !this.forced) ? 'sineOut' : 'linear';

        if (this.hasTeasingSymbol) {
            // set IMPACT case to teasing symbol
            for (let i = this.symbolTopCount; i < this.currentSymbols.length - this.symbolBottomCount; i++) {
                const symbol = this.currentSymbols[i];

                if (symbol && symbol.id + 1 == Settings.SymbolTeasing) {
                    if (this.canTeasingSymbolHit != null && this.canTeasingSymbolHit == true) {
                        symbol.goToCase(Symbol.CaseImpact);

                        // PlayList.play('SND_BaseScatterHit' + (this.teasingSymbolHit > 3 ? 3 : this.teasingSymbolHit));
                        if (this.countOfTeasingSymbols >= 3) {
                            PlayList.play("SND_BaseScatter03");
                        } else {
                            PlayList.play("SND_BaseScatterHit_" + this.index);
                        }
                    }

                    this.symbolToNoTrimConAndRunTween(symbol, speed, post, ease);
                }
            }
        }

        this.emit(ReelExtended.ReelRollFinished, this.index);

        Tweens.removeTweens(this, "position");
        Tweens.positionTween(this, {
            tweenX: [this.positionX, this.positionX, this.positionX],
            tweenY: [this.positionY, this.positionY + this.postRoll, this.positionY],
            tweenDuration: this.postRollSpeed * 2,
            callbackFunction: this.spinCompleteDelayed,
            callbackParams: [this.postRollSpeed * 2],
            callbackScope: this,
        });
    }

    //******************************************************************************************************************
    //  SPIN COMPLETE
    //******************************************************************************************************************

    protected spinCompleteDelayed(postRollDuration: number = 0): void {
        if (this.hasTeasingSymbol && this.canTeasingSymbolHit != null && this.canTeasingSymbolHit == true) {
            let delay = 40 - postRollDuration; // delay is duration of teasing symbol impact animations without postRollDuration
            this.spinCompleteTimer.stop();
            this.spinCompleteTimer.start(delay > 0 ? delay : 0);
        } else {
            this.spinComplete();
        }
    }

    protected updateReel(): void {
        this.returnSymbol(this.currentSymbols.pop());

        if (this.shouldBeStopping && !this.isStopping) {
            if (this.previousReel === null) {
                this.isStopping = true;
            } else if (this.previousReel.isStopping) {
                this.isStopping = true;
            }
        }

        this.stripIndex = (this.stripIndex - 1 + this.strip.length) % this.strip.length;

        let symbolId = this.strip[this.stripIndex];
        let symbolCase = Symbol.CaseBlur;
        let fromStrip: boolean = true;

        if (this.isStopping) {
            if (this.stoppingCounter < this.symbolBottomCount) {
                symbolId =
                    this.strip[
                        (this.data.index +
                            this.symbolBottomCount +
                            this.symbolCount -
                            this.stoppingCounter -
                            1 +
                            this.strip.length) %
                            this.strip.length
                    ];
                symbolCase =
                    this.stoppingCounter === this.symbolBottomCount - 1 ? Symbol.CaseRotation : Symbol.CaseBlur;
            } else if (this.stoppingCounter < this.symbolBottomCount + this.symbolCount) {
                symbolId = this.data.symbols[this.symbolBottomCount + this.symbolCount - this.stoppingCounter - 1];
                symbolCase = Symbol.CaseRotation;
                fromStrip = false;

                if (symbolId == Settings.SymbolTeasing) {
                    this.hasTeasingSymbol = true;
                }
            } else {
                symbolId =
                    this.strip[
                        (this.data.index +
                            this.symbolBottomCount +
                            this.symbolCount -
                            this.stoppingCounter -
                            1 +
                            this.strip.length) %
                            this.strip.length
                    ];
                symbolCase =
                    this.stoppingCounter === this.symbolBottomCount + this.symbolCount
                        ? Symbol.CaseRotation
                        : Symbol.CaseBlur;
                fromStrip = false;
            }

            this.stoppingCounter++;
        }

        this.currentSymbols.unshift(this.getSymbol(symbolId - 1, symbolCase, fromStrip));

        // this.addChild(this.currentSymbols[0]);
        this.addChildAt(this.currentSymbols[0], 0);

        for (let i = 0; i < this.currentSymbols.length; i++) {
            const symbol = this.currentSymbols[i];
            symbol.x = -symbol.offsetX;
            symbol.y = this.symbolHeight * (i - this.symbolTopCount) - symbol.offsetY;
        }

        this.sortSymbols();

        if (
            this.isStopping &&
            this.stoppingCounter === this.symbolBottomCount + this.symbolCount + this.symbolTopCount
        ) {
            this.spinPostRoll();
        } else {
            this.spinRoll();
        }
    }

    public sortSymbols(): void {
        if (!this.symbols.hasZindex) {
            return;
        }
        let addQueue: Symbol[] = [];
        this.currentSymbols.forEach((symbol) => {
            addQueue.push(<Symbol>symbol);
        });
        addQueue.sort((a, b) => a.zIndex - b.zIndex);
        addQueue.forEach((symbol) => {
            this.addChild(symbol);
        });
    }

    public symbolToNoTrimConAndRunTween(symbol: Symbol, speed: number, postRoll: number, ease: string): void {
        if (symbol && this._symbolsNoTrimContainer) {
            symbol.x += this.positionX ? this.positionX : 0;
            symbol.y += this.positionY ? this.positionY : 0;

            Tweens.positionTween(symbol, {
                tweenX: [symbol.x, symbol.x, symbol.x],
                tweenY: [symbol.y, symbol.y + postRoll, symbol.y],
                tweenDuration: speed,
                tweenEase: ease,
            });

            // this._symbolsNoTrimContainer.addChildAt(symbol, 0);
            this._symbolsNoTrimContainer.addChild(symbol);
        }
    }

    protected getSymbol(symbolId: number, symbolCase: string, fromStrip: boolean): Symbol {
        if (ReelExtended.Tease) {
            if (symbolId + 1 === Settings.SymbolTeasing) {
                symbolCase = Symbol.CaseNormal;
            } else {
                symbolCase = Symbol.CaseTease;
            }
        }
        return this.symbols.getSymbol(symbolId, symbolCase);
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/ReelExtended.ts
 **/